<script lang="ts">
    import env, { officialApiURL } from "$lib/env";
    import { tick } from "svelte";
    import { page } from "$app/state";
    import { goto } from "$app/navigation";
    import { browser } from "$app/environment";
    import { t } from "$lib/i18n/translations";
    import dialogs from "$lib/state/dialogs";
    import { link } from "$lib/state/omnibox";
    import { hapticSwitch } from "$lib/haptics";
    import { updateSetting } from "$lib/state/settings";
    import { savingHandler } from "$lib/api/saving-handler";
    import { pasteLinkFromClipboard } from "$lib/clipboard";
    import { turnstileEnabled, turnstileSolved } from "$lib/state/turnstile";

    import type { Optional } from "$lib/types/generic";
    import type { DownloadModeOption } from "$lib/types/settings";

    import ClearButton from "$components/save/buttons/ClearButton.svelte";
    import DownloadButton from "$components/save/buttons/DownloadButton.svelte";
    import Switcher from "$components/buttons/Switcher.svelte";
    import ActionButton from "$components/buttons/ActionButton.svelte";
    import CaptchaTooltip from "$components/save/CaptchaTooltip.svelte";
    import SettingsButton from "$components/buttons/SettingsButton.svelte";

    import IconMute from "$components/icons/Mute.svelte";
    import IconMusic from "$components/icons/Music.svelte";
    import IconSparkles from "$components/icons/Sparkles.svelte";
    import IconClipboard from "$components/icons/Clipboard.svelte";
    
    // Iconos para el nuevo menú de Drive
    import IconCloud from "@tabler/icons-svelte/IconCloud.svelte";
    import IconDeviceDesktop from "@tabler/icons-svelte/IconDeviceDesktop.svelte";
    import IconUsers from "@tabler/icons-svelte/IconUsers.svelte";
    import IconFolderPlus from "@tabler/icons-svelte/IconFolderPlus.svelte";
    import IconFolder from "@tabler/icons-svelte/IconFolder.svelte";
    import IconChevronDown from "@tabler/icons-svelte/IconChevronDown.svelte";
    import IconArrowRight from "@tabler/icons-svelte/IconArrowRight.svelte";

    let linkInput: Optional<HTMLInputElement>;
    const validLink = (url: string) => { try { return /^https?\:/i.test(new URL(url).protocol); } catch {} };

    let isFocused = $state(false);
    let isDisabled = $state(false);
    let isLoading = $state(false);
    let isHovered = $state(false);
    let isBotCheckOngoing = $derived($turnstileEnabled && !$turnstileSolved);
    let linkPrefill = $derived(page.url.hash.replace("#", "") || (browser ? page.url.searchParams.get("u") : "") || "");

    let downloadable = $derived(true); 
    let clearVisible = $derived($link && !isLoading);

    let isDestinationMenuOpen = $state(false);
    let selectedDestination = $state("local");
    
    // VARIABLES DE GESTIÓN DE CARPETAS
    let selectedFolderText = $state(""); 
    let actualFolderName = $state(""); 
    let actualFolderId = $state(""); // NUEVO: Guardamos el ID real para subir archivos
    let isCreatingFolder = $state(false);
    let newFolderName = $state("");
    
    // NUEVO: Array para guardar las carpetas reales
    let realDriveFolders = $state<{id: string, name: string}[]>([]);
    let isLoadingFolders = $state(false);

    const dummyFoldersFriends = ["Bandeja de Entrada", "Memes Compartidos"];

    // NUEVO: Función para pedirle a Google tus carpetas
    const loadDriveFolders = async () => {
        const token = localStorage.getItem("gdrive_token");
        if (!token) return;
        
        isLoadingFolders = true;
        try {
            // Buscamos solo carpetas, que no estén en la papelera
            const query = encodeURIComponent("mimeType='application/vnd.google-apps.folder' and trashed=false");
            const res = await fetch(`https://www.googleapis.com/drive/v3/files?q=${query}&fields=files(id,name)&orderBy=modifiedTime desc&pageSize=8`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const data = await res.json();
            
            if (data.files) {
                realDriveFolders = data.files.map((f: any) => ({ id: f.id, name: f.name }));
            }
        } catch (e) {
            console.error("Error al cargar las carpetas de Drive:", e);
        }
        isLoadingFolders = false;
    };

    const selectMode = (mode: string) => {
        selectedDestination = mode;
        selectedFolderText = ""; 
        actualFolderName = "";
        actualFolderId = "";
        isCreatingFolder = false;
        newFolderName = "";

        if (mode === "local") {
            isDestinationMenuOpen = false;
        } else if (mode === "drive_me") {
            // Cuando se elige drive_me, cargamos las carpetas de verdad
            loadDriveFolders();
        }
    };

    // MODIFICADO: Ahora aceptamos también el ID de la carpeta
    const confirmFolder = (folderName: string, folderId: string = "", isNew: boolean = false) => {
        if (isNew) {
            if (!newFolderName.trim()) return;
            actualFolderName = newFolderName.trim();
            actualFolderId = "NEW"; // Marca especial para crearla en el siguiente paso
            selectedFolderText = `Crear carpeta: ''${actualFolderName}''`;
        } else {
            actualFolderName = folderName;
            actualFolderId = folderId;
            selectedFolderText = `''${actualFolderName}''`;
        }
        isDestinationMenuOpen = false;
    };

    $effect (() => {
        if (linkPrefill) {
            linkPrefill = decodeURIComponent(linkPrefill);
            if (validLink(linkPrefill)) $link = linkPrefill;
            if (browser) goto("/", { replaceState: true });
            linkPrefill = "";
            handleCustomDownload();
        }
    });

    const pasteClipboard = async () => {
        if ($dialogs.length > 0 || isDisabled || isLoading) return;
        hapticSwitch();
        const pastedData = await pasteLinkFromClipboard();
        if (!pastedData) return;
        const linkMatch = pastedData.match(/https?\:\/\/[^\s]+/g);
        if (linkMatch) {
            $link = linkMatch[0].split('，')[0];
            await tick(); 
            handleCustomDownload();
        }
    };

    const changeDownloadMode = (mode: DownloadModeOption) => { updateSetting({ save: { downloadMode: mode } }); };

    const handleCustomDownload = () => {
        if (!validLink($link)) return;
        
        // Pasamos también el Folder ID al motor de descargas
        savingHandler({ 
            url: $link,
            destination: selectedDestination,
            folderName: actualFolderName,
            folderId: actualFolderId,
            isNewFolder: actualFolderId === "NEW"
        });
    };

    const handleKeydown = (e: KeyboardEvent) => {
        if (!linkInput || $dialogs.length > 0 || isDisabled || isLoading) return;
        if (e.metaKey || e.ctrlKey || e.key === "/") linkInput.focus();
        if (e.key === "Enter" && validLink($link) && isFocused) handleCustomDownload();
        if (["Escape", "Clear"].includes(e.key) && isFocused) $link = "";
        if (e.target === linkInput) return;
        switch (e.key) {
            case "D": pasteClipboard(); break;
            case "J": changeDownloadMode("auto"); break;
            case "K": changeDownloadMode("audio"); break;
            case "L": changeDownloadMode("mute"); break;
        }
    };
</script>

<svelte:window onkeydown={handleKeydown} />

<div id="omnibox">
    <div id="input-container" class:focused={isFocused} class:downloadable class:clear-visible={clearVisible}>
        <input
            id="link-area"
            bind:value={$link}
            bind:this={linkInput}
            oninput={() => (isFocused = true)}
            onfocus={() => (isFocused = true)}
            onblur={() => (isFocused = false)}
            spellcheck="false"
            autocomplete="off"
            autocapitalize="off"
            placeholder="pega el enlace aquí"
            disabled={isDisabled}
        />
        <ClearButton click={() => ($link = "")} />
        <div onclick={(e) => { e.preventDefault(); handleCustomDownload(); }}>
             <DownloadButton url={$link} bind:disabled={isDisabled} bind:loading={isLoading} />
        </div>
    </div>

    <!-- DESPLEGABLE DE DESTINO (ASISTENTE INTERACTIVO) -->
    <div id="destination-container">
        <button 
            id="destination-toggle" 
            class:active={isDestinationMenuOpen} 
            onclick={() => isDestinationMenuOpen = !isDestinationMenuOpen}
        >
            <div class="toggle-left">
                {#if selectedDestination === "local"}
                    <IconDeviceDesktop /> <span class="destination-label">Descarga directa ( En mi PC)</span>
                {:else if selectedDestination === "drive_me"}
                    <IconCloud /> 
                    <span class="destination-label">
                        Para mi (Google Drive)
                        {#if selectedFolderText}
                            <span class="folder-indicator"><IconArrowRight /> {selectedFolderText}</span>
                        {/if}
                    </span>
                {:else}
                    <IconUsers /> 
                    <span class="destination-label">
                        Para mis amigos
                        {#if selectedFolderText}
                            <span class="folder-indicator"><IconArrowRight /> {selectedFolderText}</span>
                        {/if}
                    </span>
                {/if}
            </div>
            <IconChevronDown class="chevron" />
        </button>

        {#if isDestinationMenuOpen}
            <div id="destination-menu" class="accordion-content">
                
                <div class="menu-section">
                    <p class="section-title">Modo Estándar</p>
                    <button class="dest-option" class:selected={selectedDestination === "local"} onclick={() => selectMode("local")}>
                        <IconDeviceDesktop /> Descarga directa ( En mi PC)
                    </button>
                </div>
                
                <div class="menu-section">
                    <p class="section-title">Ecosistema 1312sounds (Auto-conversión a WAV)</p>
                    <button class="dest-option" class:selected={selectedDestination === "drive_me"} onclick={() => selectMode("drive_me")}>
                        <IconCloud /> Para mi (Google Drive)
                    </button>
                    
                    {#if selectedDestination === "drive_me"}
                        <div class="folder-panel">
                            <p class="sub-instructions">Elige una carpeta existente o crea una nueva:</p>
                            <div class="folder-grid">
                                {#if isLoadingFolders}
                                    <span class="sub-instructions">Buscando en tu Drive...</span>
                                {:else if realDriveFolders.length === 0}
                                    <span class="sub-instructions">No se encontraron carpetas.</span>
                                {:else}
                                    {#each realDriveFolders as folder}
                                        <button class="mini-btn folder-btn" onclick={() => confirmFolder(folder.name, folder.id)}>
                                            <IconFolder /> {folder.name}
                                        </button>
                                    {/each}
                                {/if}
                            </div>
                            
                            {#if !isCreatingFolder}
                                <button class="mini-btn new-folder-trigger" onclick={() => isCreatingFolder = true}>
                                    <IconFolderPlus /> Nueva Carpeta
                                </button>
                            {:else}
                                <div class="create-folder-box">
                                    <input type="text" bind:value={newFolderName} placeholder="Escribe un nombre..." class="folder-input" />
                                    <button class="mini-btn confirm-btn" onclick={() => confirmFolder('', true)}>Crear</button>
                                </div>
                            {/if}
                        </div>
                    {/if}

                    <button class="dest-option" style="margin-top: 8px;" class:selected={selectedDestination === "drive_friends"} onclick={() => selectMode("drive_friends")}>
                        <IconUsers /> Para mis amigos
                    </button>

                    {#if selectedDestination === "drive_friends"}
                        <div class="folder-panel">
                            <p class="sub-instructions">Elige la bandeja de destino remota:</p>
                            <div class="folder-grid">
                                {#each dummyFoldersFriends as folder}
                                    <button class="mini-btn folder-btn" onclick={() => confirmFolder(folder)}>
                                        <IconFolder /> {folder}
                                    </button>
                                {/each}
                            </div>
                            
                            {#if !isCreatingFolder}
                                <button class="mini-btn new-folder-trigger" onclick={() => isCreatingFolder = true}>
                                    <IconFolderPlus /> Crear Carpeta Compartida
                                </button>
                            {:else}
                                <div class="create-folder-box">
                                    <input type="text" bind:value={newFolderName} placeholder="Escribe un nombre..." class="folder-input" />
                                    <button class="mini-btn confirm-btn" onclick={() => confirmFolder('', true)}>Crear</button>
                                </div>
                            {/if}
                        </div>
                    {/if}
                </div>
            </div>
        {/if}
    </div>

    <!-- Modos Originales de Cobalt -->
    <div id="action-container">
        <Switcher>
            <SettingsButton settingContext="save" settingId="downloadMode" settingValue="auto">
                <IconSparkles /> {$t("save.auto")}
            </SettingsButton>
            <SettingsButton settingContext="save" settingId="downloadMode" settingValue="audio">
                <IconMusic /> {$t("save.audio")}
            </SettingsButton>
            <SettingsButton settingContext="save" settingId="downloadMode" settingValue="mute">
                <IconMute /> {$t("save.mute")}
            </SettingsButton>
        </Switcher>
    </div>
</div>

<style>
    #omnibox { display: flex; flex-direction: column; max-width: 640px; width: 100%; gap: 6px; position: relative; }
    #input-container { --input-padding: 10px; display: flex; box-shadow: 0 0 0 1.5px var(--input-border) inset; outline: 1.5px solid var(--input-border); outline-offset: -1.5px; border-radius: var(--border-radius); align-items: center; gap: var(--input-padding); font-size: 14px; flex: 1; background-color: transparent; }
    #input-container:not(.clear-visible) :global(#clear-button) { display: none; }
    #input-container.clear-visible { padding-right: var(--input-padding); }
    #input-container.downloadable { padding-right: 0; }
    #input-container.focused { box-shadow: none; outline: var(--secondary) 2px solid; outline-offset: -1px; }
    #link-area { display: flex; width: 100%; margin: 0; padding: var(--input-padding) 0; padding-left: var(--input-padding); height: 18px; align-items: center; border: none; outline: none; background-color: transparent; color: var(--secondary); flex: 1; font-weight: 500; font-size: inherit; border-radius: var(--border-radius); }
    #link-area::placeholder { color: var(--gray); opacity: 1; }
    #destination-container { background-color: #121212; border: 1px solid var(--input-border); border-radius: var(--border-radius); overflow: hidden; margin-bottom: 4px; }
    
    #destination-toggle { width: 100%; display: flex; justify-content: space-between; align-items: center; background: transparent; border: none; padding: 12px 16px; color: #ffffff; cursor: pointer; font-size: 14px; font-weight: 600; transition: background 0.2s; }
    #destination-toggle:hover { background-color: rgba(255,255,255,0.05); }
    
    .toggle-left { display: flex; align-items: center; gap: 8px; }
    .toggle-left :global(svg) { width: 20px; height: 20px; stroke-width: 1.5px; color: #a1a1aa; }
    
    .destination-label { display: flex; align-items: center; }
    
    /* ESTILOS DE LA NUEVA FLECHA BLINDADA EN BLANCO (#ffffff) */
    .folder-indicator { display: flex; align-items: center; gap: 6px; color: #a1a1aa; font-weight: 400; margin-left: 8px; font-size: 13px; }
    .folder-indicator :global(svg) { 
        width: 14px !important; 
        height: 14px !important; 
        color: #ffffff !important; 
        stroke: #ffffff !important; 
        stroke-width: 2.5px !important; 
    }

    #destination-toggle .chevron { transition: transform 0.3s ease; color: #a1a1aa; }
    #destination-toggle.active .chevron { transform: rotate(180deg); }
    .accordion-content { padding: 0 16px 16px 16px; display: flex; flex-direction: column; gap: 12px; border-top: 1px solid rgba(255,255,255,0.05); background-color: #0d0d0d; }
    .menu-section { display: flex; flex-direction: column; gap: 6px; margin-top: 10px; }
    .section-title { font-size: 11px; color: #a1a1aa; text-transform: uppercase; letter-spacing: 1px; margin: 0 0 4px 4px; font-weight: 800; }
    .dest-option { display: flex; align-items: center; gap: 10px; background: #18181b; border: 1px solid transparent; border-radius: 8px; padding: 10px 14px; color: #a1a1aa; font-size: 13.5px; font-weight: 600; cursor: pointer; text-align: left; transition: all 0.2s; }
    .dest-option :global(svg) { width: 18px; height: 18px; opacity: 0.7; }
    .dest-option:hover { background: #27272a; color: #ffffff; }
    .dest-option.selected { background: rgba(207, 34, 46, 0.1); border: 1px solid rgba(207, 34, 46, 0.3); color: #ffffff !important; }
    .dest-option.selected :global(svg) { color: var(--primary); opacity: 1; }
    .folder-panel { background-color: #121212; border-radius: 8px; padding: 12px; border: 1px solid rgba(255,255,255,0.05); display: flex; flex-direction: column; gap: 10px; margin-left: 10px; margin-right: 10px; }
    .sub-instructions { margin: 0; font-size: 12px; color: #a1a1aa; }
    .folder-grid { display: flex; flex-wrap: wrap; gap: 6px; }
    .mini-btn { display: flex; align-items: center; gap: 6px; background: #222227; color: #a1a1aa; font-size: 12px; font-weight: 600; padding: 6px 12px; border-radius: 6px; border: none; cursor: pointer; transition: 0.2s; }
    .mini-btn:hover { background: #3f3f46; color: #ffffff; }
    .mini-btn :global(svg) { width: 14px; height: 14px; }
    .new-folder-trigger { background: transparent; border: 1px dashed #3f3f46; color: #a1a1aa; }
    .new-folder-trigger:hover { background: rgba(255,255,255,0.05); border-color: #ffffff; color: #ffffff; }
    .create-folder-box { display: flex; gap: 8px; }
    .folder-input { flex: 1; background: #000000; border: 1px solid #3f3f46; border-radius: 6px; color: white; padding: 6px 10px; font-size: 12px; outline: none; }
    .folder-input:focus { border-color: var(--primary); }
    .confirm-btn { background: var(--primary); color: white; border: none; }
    .confirm-btn:hover { background: #be1e2d; color: white; }
    #action-container { display: flex; flex-direction: row; justify-content: space-between; }
</style>